import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Les balises og:/twitter: statiques de index.html servent de repli aux crawlers sans JS.
 * Une fois React monté, on retire ces doublons pour ne laisser que celles gérées par Helmet
 * (sinon deux og:description coexistent et les crawlers JS lisent la première, générique).
 */
const SOCIAL_SELECTORS = [
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:image"]',
  'meta[property="og:url"]',
  'meta[property="og:type"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:image"]',
  'meta[name="twitter:url"]',
  'meta[name="description"]',
];

const dedupeStaticSocialTags = () => {
  SOCIAL_SELECTORS.forEach((selector) => {
    const tags = Array.from(document.head.querySelectorAll<HTMLMetaElement>(selector));
    const hasHelmetTag = tags.some((tag) => tag.hasAttribute('data-rh'));
    if (!hasHelmetTag) return;
    tags.forEach((tag) => {
      if (!tag.hasAttribute('data-rh')) tag.remove();
    });
  });
};

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  /** Titre optimisé pour le partage social (défaut : title). */
  ogTitle?: string;
  /** Description optimisée pour le partage social (défaut : description). Idéal 60-110 caractères. */
  ogDescription?: string;
  /** Titre Twitter/X (défaut : ogTitle puis title). */
  twitterTitle?: string;
  /** Description Twitter/X (défaut : ogDescription puis description). Max ~200 caractères. */
  twitterDescription?: string;
  noindex?: boolean;
  /** Par défaut une page noindex reste "follow" pour que Google suive les liens de repli. */
  nofollow?: boolean;
  // Props spécifiques aux articles
  type?: 'website' | 'article';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
}

const SEO = ({ 
  title, 
  description, 
  keywords, 
  canonical, 
  ogImage,
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
  noindex = false,
  nofollow = false,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  section,
}: SEOProps) => {
  const siteUrl = 'https://www.mind-medical.fr';
  const siteTitle = "Mind Medical";
  const fullTitle = title.includes("Mind Medical") ? title : `${title} | ${siteTitle}`;
  const defaultImage = `${siteUrl}/og-image.jpg`;
  // Toujours canonicaliser vers le domaine www (sans query string ni hash)
  const canonicalUrl = canonical
    ? `${siteUrl}${canonical}`
    : `${siteUrl}${typeof window !== 'undefined' ? window.location.pathname : '/'}`;
  const ogImageUrl = ogImage || defaultImage;

  // Social : titres/descriptions dédiés, avec repli sur les métadonnées SEO de la page
  const socialTitle = ogTitle ? (ogTitle.includes(siteTitle) ? ogTitle : `${ogTitle} | ${siteTitle}`) : fullTitle;
  const socialDescription = ogDescription || description;
  const xTitle = twitterTitle ? (twitterTitle.includes(siteTitle) ? twitterTitle : `${twitterTitle} | ${siteTitle}`) : socialTitle;
  const xDescription = twitterDescription || socialDescription;

  useEffect(() => {
    // Helmet écrit dans le <head> après le rendu : on repasse une frame plus tard.
    dedupeStaticSocialTags();
    const raf = requestAnimationFrame(() => dedupeStaticSocialTags());
    const timer = window.setTimeout(() => dedupeStaticSocialTags(), 300);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [socialTitle, socialDescription, xTitle, xDescription, ogImageUrl, canonicalUrl, description]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Canonical URL - Important for SEO.
          Jamais de canonical sur une page noindex : signaux contradictoires. */}
      {!noindex && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content={`noindex, ${nofollow ? 'nofollow' : 'follow'}`} />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={ogTitle || title} />
      <meta property="og:site_name" content="Mind Medical" />
      <meta property="og:locale" content="fr_FR" />
      
      {/* Open Graph Article specific */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && section && (
        <meta property="article:section" content={section} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={xTitle} />
      <meta name="twitter:description" content={xDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={twitterTitle || ogTitle || title} />
      {author && <meta name="twitter:creator" content={author} />}

      {/* Additional SEO */}
      <meta name="language" content="French" />
      <meta name="author" content={author || "Mind Medical"} />
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="theme-color" content="#0EA5E9" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Note: preconnect/dns-prefetch hints for fonts and Supabase are declared statically in index.html to avoid duplicate (and Lighthouse-flagged "unused") preconnects. */}
    </Helmet>
  );
};

export default SEO;
